import React from 'react';
import { Plus } from 'lucide-react';
import { Medication } from '../../../../dtos/patientmr/medication.dto';
import NewEntryForm from './NewEntryForm';
import EntryList from './EntryList';
import QuickAddMenu from './QuickAddMenu';
import ShiftStrip from './ShiftStrip';
import { useChartForm } from '../ChartFormContext';
import { Entry } from '../types';

interface ActiveVisitChartProps {
  hasActiveVisit: boolean;
  isMobile: boolean;
  inPlaceEditingEntryId: string | number | null;
  isAddingNewEntry: boolean;
  handleAddEntry: (type: string, visitId?: string | number) => void;
  selectedEntryType: string | null;
  vitalFormData: any;
  setVitalFormData: (data: any) => void;
  marFormData: any;
  setMarFormData: (data: any) => void;
  feedFormData: any;
  setFeedFormData: (data: any) => void;
  notesFormData: any;
  setNotesFormData: (data: any) => void;
  woundFormData: any;
  setWoundFormData: (data: any) => void;
  incidentFormData: any;
  setIncidentFormData: (data: any) => void;
  summaryFormData: any;
  setSummaryFormData: (data: any) => void;
  measurementsFormData: any;
  setMeasurementsFormData: (data: any) => void;
  editingEntryId: string | number | null;
  cancelAddingEntry: () => void;
  handleSubmitNewEntry: () => void;
  loadingEntries: boolean;
  currentVisitEntries: Entry[];
  handleEditEntry: (entry: Entry) => void;
  onFlagEntry: (entryId: string | number) => void;
  onInvalidateEntry: (entryId: string | number) => void;
  setInPlaceEditingEntryId: (id: string | number | null) => void;
  renderEntryContent: (entry: Entry) => React.ReactNode;
  renderSelectedForm: (entryId: number | string) => React.ReactNode;
  cancelInPlaceEdit: () => void;
  handleSaveInPlaceEdit: (entryId: string | number) => void;
  chartingStatus: 'DRAFT' | 'SUBMITTED' | 'QA' | 'COMPLETE';
  saving?: boolean;
  patientMedications?: Medication[];
  isReadOnly?: boolean;
  /** Phase 18U U6: open the Add sheet (phones and field-mode tablets use it instead of tiles) */
  onOpenAdd?: () => void;
  /** Phase 18U U2: the visit screen's action bar has the Add button */
  hideAddButton?: boolean;
  /** Phase 18U U9: the open form's resume banner and draft time */
  draftNotice?: React.ReactNode;
  draftSavedAt?: Date | null;
  /** Phase 18U U4: the entry just saved (highlighted and scrolled into view) */
  highlightEntryId?: string | number | null;
  /** Phase 18U U18: landscape tablet — timeline left, "This shift" and the Add tiles right */
  sideBySide?: boolean;
}

const ActiveVisitChart: React.FC<ActiveVisitChartProps> = ({
  hasActiveVisit,
  isMobile,
  inPlaceEditingEntryId,
  isAddingNewEntry,
  handleAddEntry,
  selectedEntryType,
  vitalFormData,
  setVitalFormData,
  marFormData,
  setMarFormData,
  feedFormData,
  setFeedFormData,
  notesFormData,
  setNotesFormData,
  woundFormData,
  setWoundFormData,
  incidentFormData,
  setIncidentFormData,
  summaryFormData,
  setSummaryFormData,
  measurementsFormData,
  setMeasurementsFormData,
  editingEntryId,
  cancelAddingEntry,
  handleSubmitNewEntry,
  loadingEntries,
  currentVisitEntries,
  handleEditEntry,
  onFlagEntry,
  onInvalidateEntry,
  setInPlaceEditingEntryId,
  renderEntryContent,
  renderSelectedForm,
  cancelInPlaceEdit,
  handleSaveInPlaceEdit,
  chartingStatus,
  saving = false,
  patientMedications = [],
  isReadOnly = false,
  onOpenAdd,
  hideAddButton = false,
  draftNotice = null,
  draftSavedAt = null,
  highlightEntryId = null,
  sideBySide = false
}) => {
  const chart = useChartForm();
  return (
    <div className="mb-6 border-b pb-6">
      <div className="mb-4">
        <div className="flex justify-between items-center mb-3">
          <div className="text-base font-semibold text-slate-800">Current Visit</div>
        </div>
        
        {/* Phase 18U U6: phones and field-mode tablets add through the Add sheet */}
        {hasActiveVisit && isMobile && onOpenAdd && !hideAddButton && !sideBySide && (
          <button
            type="button"
            onClick={onOpenAdd}
            disabled={isReadOnly}
            className="mb-4 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-slate-800 text-base font-semibold text-white disabled:opacity-50"
            data-testid="open-add-sheet"
          >
            <Plus size={20} aria-hidden="true" />Add to chart
          </button>
        )}
        {/* Quick Entry Buttons on desktop (Phase 18 M8 order by discipline) */}
        {hasActiveVisit && !sideBySide && !(isMobile && onOpenAdd) && !inPlaceEditingEntryId && !isAddingNewEntry && (
          <QuickAddMenu
            onAdd={(type) => handleAddEntry(type)}
            disabled={inPlaceEditingEntryId !== null || isAddingNewEntry || chartingStatus !== 'DRAFT'}
            isMobile={isMobile}
            discipline={chart?.visit?.discipline}
            visitType={chart?.visit?.visitType}
            entries={currentVisitEntries}
            palliative={chart?.palliative}
          />
        )}
      </div>

      {!hasActiveVisit ? (
        // No active visit message
        <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-md">
          <p>No active visit selected. Please start a visit to enable charting.</p>
        </div>
      ) : (
        <div className={sideBySide ? 'grid grid-cols-[minmax(0,1fr)_20rem] items-start gap-4' : ''} data-testid={sideBySide ? 'visit-two-pane' : undefined}>

          {/* Main column - Entries */}
          <div className="min-w-0">
            {/* New Editable Entry Form */}
            {isAddingNewEntry && (
              <NewEntryForm
                selectedEntryType={selectedEntryType}
                vitalFormData={vitalFormData}
                setVitalFormData={setVitalFormData}
                marFormData={marFormData}
                setMarFormData={setMarFormData}
                feedFormData={feedFormData}
                setFeedFormData={setFeedFormData}
                notesFormData={notesFormData}
                setNotesFormData={setNotesFormData}
                woundFormData={woundFormData}
                setWoundFormData={setWoundFormData}
                incidentFormData={incidentFormData}
                setIncidentFormData={setIncidentFormData}
                summaryFormData={summaryFormData}
                setSummaryFormData={setSummaryFormData}
                measurementsFormData={measurementsFormData}
                setMeasurementsFormData={setMeasurementsFormData}
                editingEntryId={editingEntryId}
                cancelAddingEntry={cancelAddingEntry}
                handleSubmitNewEntry={handleSubmitNewEntry}
                onCancel={cancelAddingEntry}
                onSave={handleSubmitNewEntry}
                isMobile={isMobile}
                saving={saving}
                patientMedications={patientMedications}
                isReadOnly={isReadOnly}
                draftNotice={draftNotice}
                savedAt={draftSavedAt}
              />
            )}
            {/* Display Current Visit Entries */}
            {/* Phase 18U U11: what has been charted this shift, last vitals, PRN effects due */}
            {!sideBySide && <ShiftStrip entries={currentVisitEntries} onRecordEffect={chart?.chartEditable ? chart.recordEffect : undefined} />}
            {/* Phase 18U U4: the spinner shows on the first load only; saves never empty the list */}
            {loadingEntries && currentVisitEntries.length === 0 ? (
              <div className="text-center py-4">
                <div className="spinner"></div>
                <div className="text-sm text-gray-500 mt-2">Loading entries...</div>
              </div>
            ) : currentVisitEntries.length > 0 ? (
              // Phase 18 M8: grouped by the hour of observed time
              <EntryList
                entries={currentVisitEntries}
                handleEditEntry={handleEditEntry}
                handleFlagEntry={onFlagEntry}
                handleInvalidateEntry={onInvalidateEntry}
                inPlaceEditingEntryId={inPlaceEditingEntryId}
                setInPlaceEditingEntryId={setInPlaceEditingEntryId}
                renderEntryContent={renderEntryContent}
                renderSelectedForm={renderSelectedForm}
                cancelInPlaceEdit={cancelInPlaceEdit}
                handleSaveInPlaceEdit={handleSaveInPlaceEdit}
                isMobile={isMobile}
                saving={saving}
                locked={chartingStatus !== 'DRAFT'}
                highlightEntryId={highlightEntryId}
              />
            ) : (
              <div className="text-center text-gray-500 py-6">
                <div className="text-sm">No entries for this visit yet. {isMobile && onOpenAdd ? 'Tap Add to chart vitals, meds or a note.' : 'Use a button above (Vitals, Meds, Note …) to add one.'}</div>
              </div>
            )}
          </div>
          {sideBySide && (
            <aside className="sticky space-y-3 self-start" style={{ top: 'calc(var(--chart-sticky-top, 0px) + 0.5rem)' }} aria-label="Add and this shift">
              <ShiftStrip entries={currentVisitEntries} onRecordEffect={chart?.chartEditable ? chart.recordEffect : undefined} />
              {!inPlaceEditingEntryId && !isAddingNewEntry && (
                <QuickAddMenu
                  onAdd={(type) => handleAddEntry(type)}
                  disabled={chartingStatus !== 'DRAFT' || isReadOnly}
                  isMobile
                  discipline={chart?.visit?.discipline}
                  visitType={chart?.visit?.visitType}
                  entries={currentVisitEntries}
                  palliative={chart?.palliative}
                />
              )}
            </aside>
          )}
        </div>
      )}
    </div>
  );
};

export default ActiveVisitChart;
